/**
 * SsoReentryBanner — one-line ribbon for visitors who already hold an active
 * shared-tenant SSO session (signed in via ERIO on the shared Auth0 tenant).
 *
 * Cross-app signal contract (see /app/memory/ekselens-erio-reconciliation.md §SSO-signal):
 *   ERIO, on a successful SSO sign-in, sets a NON-httpOnly cookie scoped to the
 *   shared parent domain so both apps can read it:
 *       ek_sso=1                     (presence flag)         Domain=.ekselens.com
 *       ek_sso_name=<display name>   (optional, URL-encoded) Domain=.ekselens.com
 *   It is cleared on sign-out. This banner is purely additive — if the cookie is
 *   absent (e.g. in the preview environment or before ERIO ships the forwarder)
 *   nothing renders.
 *
 * Dismissal is remembered for the browser session only (sessionStorage), so a
 * returning visitor sees it again on their next visit but isn't nagged per page.
 */
import React, { useEffect, useState } from "react";
import { LogIn, X } from "lucide-react";
import { erioLoginUrl } from "../lib/ssoSession";

const DISMISS_KEY = "ek-sso-reentry-dismissed";

const readCookie = (name) => {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
};

export default function SsoReentryBanner() {
  const [visible, setVisible] = useState(false);
  const [name, setName] = useState("");

  useEffect(() => {
    // Never show inside the admin console.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) return;
    let dismissed = false;
    try { dismissed = window.sessionStorage.getItem(DISMISS_KEY) === "1"; } catch {/*noop*/}
    if (dismissed) return;
    if (readCookie("ek_sso") === "1") {
      setName(readCookie("ek_sso_name") || "");
      setVisible(true);
    }
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { window.sessionStorage.setItem(DISMISS_KEY, "1"); } catch {/*noop*/}
  };

  // While the ribbon is visible, push the fixed header + main content down by
  // its height so it sits ABOVE the header instead of being hidden behind it.
  useEffect(() => {
    const cls = "ek-has-top-banner";
    if (visible) document.body.classList.add(cls);
    else document.body.classList.remove(cls);
    return () => document.body.classList.remove(cls);
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 top-0 z-[60] bg-[#7737F6] text-white h-10 flex items-center"
      role="status"
      data-testid="sso-reentry-banner"
    >
      <div className="ek-container flex items-center gap-3 text-[13px] w-full">
        <LogIn size={14} className="shrink-0 opacity-90" />
        <p className="flex-1 leading-snug">
          Welcome back{name ? `, ${name}` : ""} — you&rsquo;re already signed in.{" "}
          <a
            href={erioLoginUrl()}
            className="underline underline-offset-2 font-medium hover:opacity-90"
            data-testid="sso-reentry-open-erio"
          >
            Open in ERIO →
          </a>
        </p>
        <button
          type="button"
          onClick={dismiss}
          className="shrink-0 opacity-80 hover:opacity-100"
          aria-label="Dismiss"
          data-testid="sso-reentry-dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
